import React, { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Plus, FileText, TrendingUp, Clock } from 'lucide-react'
import Shell from '../components/Shell'
import { useCases, type CaseSummary, type StepStatus } from '../hooks/useCases'
import styles from './QuickCheckHomePage.module.css'

/**
 * Get CSS class for step indicator based on status
 */
const getStepClass = (status: StepStatus): string => {
  switch (status) {
    case 'complete': return styles.stepComplete
    case 'active': return styles.stepActive
    case 'error': return styles.stepError
    default: return styles.stepPending
  }
}

const getRelativeTime = (date: Date) => {
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffDays > 0) return `${diffDays}d ago`
  if (diffHours > 0) return `${diffHours}h ago`
  if (diffMins > 0) return `${diffMins}m ago`
  return 'just now'
}

const CaseCard: React.FC<{ caseData: CaseSummary }> = ({ caseData }) => {
  const navigate = useNavigate()
  const { steps, completedSteps, totalSteps } = caseData

  const handleClick = () => {
    navigate(`/quick-check/${caseData.id}/documents`)
  }

  return (
    <div className={styles.caseCard} onClick={handleClick}>
      <div className={styles.cardHeader}>
        <h3 className={styles.cardTitle}>{caseData.borrowerName}</h3>
        <span className={styles.cardTime}>{getRelativeTime(caseData.updatedAt)}</span>
      </div>

      <div className={styles.cardProgress}>
        <div className={styles.stepDots}>
          <div className={`${styles.stepDot} ${getStepClass(steps.documents)}`} title="Documents" />
          <div className={`${styles.stepDot} ${getStepClass(steps.income)}`} title="Income" />
          <div className={`${styles.stepDot} ${getStepClass(steps.cashflow)}`} title="Cash Flow" />
          <div className={`${styles.stepDot} ${getStepClass(steps.assets)}`} title="Assets" />
          <div className={`${styles.stepDot} ${getStepClass(steps.debt)}`} title="Debt" />
        </div>
        <span className={styles.progressLabel}>{completedSteps}/{totalSteps} complete</span>
      </div>

      <div className={styles.cardFooter}>
        <span className={styles.currentStep}>{caseData.currentStep}</span>
        <span className={styles.statusMessage}>{caseData.statusMessage}</span>
      </div>
    </div>
  )
}

const QuickCheckHomePage: React.FC = () => {
  const navigate = useNavigate()
  const { cases, loading, error } = useCases()
  const [searchQuery, setSearchQuery] = useState('')

  // Filter cases based on search
  const filteredCases = useMemo(() => {
    if (!searchQuery.trim()) return cases
    const query = searchQuery.toLowerCase()
    return cases.filter(c =>
      c.borrowerName.toLowerCase().includes(query) ||
      c.currentStep.toLowerCase().includes(query) ||
      c.id.toLowerCase().includes(query)
    )
  }, [cases, searchQuery])

  // Stats
  const stats = useMemo(() => {
    const total = cases.length
    const completed = cases.filter(c => c.completedSteps === c.totalSteps).length
    const inProgress = cases.filter(c => c.completedSteps > 0 && c.completedSteps < c.totalSteps).length
    const recent = cases.filter(c => {
      const hourAgo = Date.now() - 60 * 60 * 1000
      return c.updatedAt.getTime() > hourAgo
    }).length
    return { total, completed, inProgress, recent }
  }, [cases])

  const handleCreateCase = () => {
    navigate('/quick-check/new')
  }

  return (
    <Shell variant="app" showFooter={false}>
      <div className={styles.container}>
        {/* Header */}
        <header className={styles.header}>
          <div className={styles.headerContent}>
            <h1 className={styles.title}>Quick Check</h1>
            <p className={styles.subtitle}>Income verification at a glance</p>
          </div>
          <button className={styles.newCaseButton} onClick={handleCreateCase}>
            <Plus size={18} />
            New Case
          </button>
        </header>

        {/* Stats Row */}
        <div className={styles.statsRow}>
          <div className={styles.statCard}>
            <FileText size={20} className={styles.statIcon} />
            <div className={styles.statContent}>
              <span className={styles.statValue}>{stats.total}</span>
              <span className={styles.statLabel}>Total Cases</span>
            </div>
          </div>
          <div className={styles.statCard}>
            <TrendingUp size={20} className={styles.statIcon} />
            <div className={styles.statContent}>
              <span className={styles.statValue}>{stats.inProgress}</span>
              <span className={styles.statLabel}>In Progress</span>
            </div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.completedIcon}>✓</div>
            <div className={styles.statContent}>
              <span className={styles.statValue}>{stats.completed}</span>
              <span className={styles.statLabel}>Completed</span>
            </div>
          </div>
          <div className={styles.statCard}>
            <Clock size={20} className={styles.statIcon} />
            <div className={styles.statContent}>
              <span className={styles.statValue}>{stats.recent}</span>
              <span className={styles.statLabel}>Updated Recently</span>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className={styles.searchSection}>
          <div className={styles.searchBox}>
            <Search size={18} className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Search by borrower name, case ID, or status..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
            />
          </div>
        </div>

        {/* Cases Grid */}
        <div className={styles.casesSection}>
          {loading ? (
            <div className={styles.loadingState}>
              <div className={styles.spinner} />
              <span>Loading cases...</span>
            </div>
          ) : error ? (
            <div className={styles.errorState}>
              <p>Failed to load cases</p>
              <span>{error.message}</span>
            </div>
          ) : filteredCases.length === 0 ? (
            <div className={styles.emptyState}>
              {searchQuery ? (
                <>
                  <p>No cases match "{searchQuery}"</p>
                  <button onClick={() => setSearchQuery('')}>Clear search</button>
                </>
              ) : (
                <>
                  <p>No cases yet</p>
                  <button onClick={handleCreateCase}>Create your first case</button>
                </>
              )}
            </div>
          ) : (
            <div className={styles.casesGrid}>
              {filteredCases.map((caseData) => (
                <CaseCard key={caseData.id} caseData={caseData} />
              ))}
            </div>
          )}
        </div>
      </div>
    </Shell>
  )
}

export default QuickCheckHomePage
